import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, SlidersHorizontal, Trash2 } from 'lucide-react';
import {
  formatAmount,
  parseAmountToCents,
  type ModifierGroup,
  type ModifierOption,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useModifierGroups } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import { useCurrency } from '../lib/currency';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Spinner,
  Textarea,
  Toggle,
} from '../components/ui';

interface GroupDraft {
  id?: string;
  name: string;
  description: string;
  isRequired: boolean;
  minSelections: number;
  maxSelections: string;
  allowQuantity: boolean;
  isActive: boolean;
}

interface OptionDraft {
  id?: string;
  groupId: string;
  name: string;
  price: string;
  isDefault: boolean;
  isActive: boolean;
}

const EMPTY_GROUP: GroupDraft = {
  name: '',
  description: '',
  isRequired: false,
  minSelections: 0,
  maxSelections: '',
  allowQuantity: false,
  isActive: true,
};

/** Reusable modifier groups shared across menu items. */
export function ModifiersAdminPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { money, symbol, decimals } = useCurrency();
  const { data: groups, isLoading, error, refetch } = useModifierGroups(true);

  const [groupDraft, setGroupDraft] = useState<GroupDraft | null>(null);
  const [optionDraft, setOptionDraft] = useState<OptionDraft | null>(null);
  const [removingGroup, setRemovingGroup] = useState<ModifierGroup | null>(null);
  const [removingOption, setRemovingOption] = useState<ModifierOption | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['modifier-groups'] });
    void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
  };
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const saveGroup = useMutation({
    mutationFn: (value: GroupDraft) => {
      const body = {
        name: value.name.trim(),
        description: value.description.trim() || null,
        isRequired: value.isRequired,
        minSelections: value.minSelections,
        maxSelections: value.maxSelections ? Number(value.maxSelections) : null,
        allowQuantity: value.allowQuantity,
        isActive: value.isActive,
      };
      return value.id ? api.modifiers.updateGroup(value.id, body) : api.modifiers.createGroup(body);
    },
    onSuccess: () => {
      invalidate();
      setGroupDraft(null);
      toast.success('Modifier group saved.');
    },
    onError: (caught) => failed(caught, 'Could not save that group.'),
  });

  const saveOption = useMutation({
    mutationFn: (value: OptionDraft) => {
      const body = {
        name: value.name.trim(),
        priceDeltaCents: parseAmountToCents(value.price || '0'),
        isDefault: value.isDefault,
        isActive: value.isActive,
      };
      return value.id
        ? api.modifiers.updateOption(value.id, body)
        : api.modifiers.createOption(value.groupId, body);
    },
    onSuccess: () => {
      invalidate();
      setOptionDraft(null);
      toast.success('Option saved.');
    },
    onError: (caught) => failed(caught, 'Could not save that option.'),
  });

  const removeGroup = useMutation({
    mutationFn: (id: string) => api.modifiers.removeGroup(id),
    onSuccess: () => {
      invalidate();
      setRemovingGroup(null);
      toast.success('Group deleted.');
    },
    onError: (caught) => failed(caught, 'Could not delete that group.'),
  });

  const removeOption = useMutation({
    mutationFn: (id: string) => api.modifiers.removeOption(id),
    onSuccess: () => {
      invalidate();
      setRemovingOption(null);
    },
    onError: (caught) => failed(caught, 'Could not delete that option.'),
  });

  const rules = (group: ModifierGroup) => {
    const parts = [group.isRequired ? 'Required' : 'Optional'];
    if (group.minSelections > 0) parts.push(`min ${group.minSelections}`);
    parts.push(group.maxSelections === null ? 'unlimited' : `max ${group.maxSelections}`);
    if (group.allowQuantity) parts.push('quantities allowed');
    return parts.join(' · ');
  };

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Modifiers</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Groups are reusable — attach one group to as many menu items as you like.
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setGroupDraft({ ...EMPTY_GROUP })}>
          New group
        </Button>
      </header>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {groups && groups.length === 0 ? (
        <EmptyState
          icon={<SlidersHorizontal size={30} />}
          title="No modifier groups"
          message="Create a group such as Flavour or Cooking Preference."
        />
      ) : null}

      <div className="space-y-4">
        {(groups ?? []).map((group) => (
          <Card key={group.id}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="font-display text-xl text-ink">
                  {group.name}
                  {!group.isActive ? <Badge className="ml-2" tone="neutral">Inactive</Badge> : null}
                </h2>
                <p className="mt-0.5 text-xs text-ink-faint">{rules(group)}</p>
                {group.description ? (
                  <p className="mt-1 text-sm text-ink-soft">{group.description}</p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  icon={<Plus size={14} />}
                  onClick={() =>
                    setOptionDraft({
                      groupId: group.id,
                      name: '',
                      price: '0.00',
                      isDefault: false,
                      isActive: true,
                    })
                  }
                >
                  Option
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Pencil size={14} />}
                  onClick={() =>
                    setGroupDraft({
                      id: group.id,
                      name: group.name,
                      description: group.description ?? '',
                      isRequired: group.isRequired,
                      minSelections: group.minSelections,
                      maxSelections: group.maxSelections === null ? '' : String(group.maxSelections),
                      allowQuantity: group.allowQuantity,
                      isActive: group.isActive,
                    })
                  }
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 size={14} />}
                  onClick={() => setRemovingGroup(group)}
                >
                  Delete
                </Button>
              </div>
            </div>

            <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {group.options.length === 0 ? (
                <p className="text-sm text-ink-faint">No options yet.</p>
              ) : null}
              {group.options.map((option) => (
                <div
                  key={option.id}
                  className="flex items-center gap-2 rounded-lg border border-line bg-canvas px-3 py-2"
                >
                  <span className="flex-1 text-sm text-ink">
                    {option.name}
                    {option.isDefault ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-accent">default</span>
                    ) : null}
                    {!option.isActive ? (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-ink-faint">off</span>
                    ) : null}
                  </span>
                  <span className="tnum text-sm text-accent">
                    {option.priceDeltaCents === 0
                      ? 'Free'
                      : `${option.priceDeltaCents > 0 ? '+' : '−'}${money(Math.abs(option.priceDeltaCents))}`}
                  </span>
                  <button
                    type="button"
                    aria-label="Edit option"
                    onClick={() =>
                      setOptionDraft({
                        id: option.id,
                        groupId: group.id,
                        name: option.name,
                        price: formatAmount(option.priceDeltaCents, decimals),
                        isDefault: option.isDefault,
                        isActive: option.isActive,
                      })
                    }
                    className="rounded p-1 text-ink-faint transition hover:text-accent"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    type="button"
                    aria-label="Delete option"
                    onClick={() => setRemovingOption(option)}
                    className="rounded p-1 text-ink-faint transition hover:text-danger"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <Modal
        open={groupDraft !== null}
        onClose={() => setGroupDraft(null)}
        title={groupDraft?.id ? 'Edit modifier group' : 'New modifier group'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setGroupDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saveGroup.isPending}
              disabled={!groupDraft?.name.trim()}
              onClick={() => groupDraft && saveGroup.mutate(groupDraft)}
            >
              Save
            </Button>
          </>
        }
      >
        {groupDraft ? (
          <div className="space-y-4">
            <Field label="Name">
              <Input
                autoFocus
                value={groupDraft.name}
                onChange={(event) => setGroupDraft({ ...groupDraft, name: event.target.value })}
                placeholder="Toppings"
              />
            </Field>
            <Field label="Description">
              <Textarea
                rows={2}
                value={groupDraft.description}
                onChange={(event) => setGroupDraft({ ...groupDraft, description: event.target.value })}
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Minimum selections">
                <Input
                  type="number"
                  min={0}
                  value={groupDraft.minSelections}
                  onChange={(event) =>
                    setGroupDraft({ ...groupDraft, minSelections: Number(event.target.value) })
                  }
                />
              </Field>
              <Field label="Maximum selections" hint="Leave empty for unlimited.">
                <Input
                  type="number"
                  min={1}
                  value={groupDraft.maxSelections}
                  onChange={(event) =>
                    setGroupDraft({ ...groupDraft, maxSelections: event.target.value })
                  }
                />
              </Field>
            </div>
            <div className="space-y-3">
              <Toggle
                checked={groupDraft.isRequired}
                onChange={(isRequired) => setGroupDraft({ ...groupDraft, isRequired })}
                label="Required — the item cannot be ordered without a choice"
              />
              <Toggle
                checked={groupDraft.allowQuantity}
                onChange={(allowQuantity) => setGroupDraft({ ...groupDraft, allowQuantity })}
                label="Allow quantities (double sauce, triple cheese…)"
              />
              <Toggle
                checked={groupDraft.isActive}
                onChange={(isActive) => setGroupDraft({ ...groupDraft, isActive })}
                label="Active"
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={optionDraft !== null}
        onClose={() => setOptionDraft(null)}
        title={optionDraft?.id ? 'Edit option' : 'New option'}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setOptionDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saveOption.isPending}
              disabled={!optionDraft?.name.trim()}
              onClick={() => optionDraft && saveOption.mutate(optionDraft)}
            >
              Save
            </Button>
          </>
        }
      >
        {optionDraft ? (
          <div className="space-y-4">
            <Field label="Name">
              <Input
                autoFocus
                value={optionDraft.name}
                onChange={(event) => setOptionDraft({ ...optionDraft, name: event.target.value })}
                placeholder="Chocolate Sauce"
              />
            </Field>
            <Field label="Price adjustment" hint="Zero for a free option; negative for a discount.">
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint">{symbol}</span>
                <Input
                  inputMode="decimal"
                  className="pl-8"
                  value={optionDraft.price}
                  onChange={(event) => setOptionDraft({ ...optionDraft, price: event.target.value })}
                />
              </div>
            </Field>
            <Toggle
              checked={optionDraft.isDefault}
              onChange={(isDefault) => setOptionDraft({ ...optionDraft, isDefault })}
              label="Pre-selected by default"
            />
            <Toggle
              checked={optionDraft.isActive}
              onChange={(isActive) => setOptionDraft({ ...optionDraft, isActive })}
              label="Active"
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={removingGroup !== null}
        title={`Delete "${removingGroup?.name ?? ''}"?`}
        message="The group and its options are removed from every menu item that uses them. Past orders keep their own snapshots."
        confirmLabel="Delete group"
        loading={removeGroup.isPending}
        onCancel={() => setRemovingGroup(null)}
        onConfirm={() => removingGroup && removeGroup.mutate(removingGroup.id)}
      />

      <ConfirmDialog
        open={removingOption !== null}
        title={`Delete "${removingOption?.name ?? ''}"?`}
        message="Past orders keep the option name and price they were charged."
        confirmLabel="Delete option"
        loading={removeOption.isPending}
        onCancel={() => setRemovingOption(null)}
        onConfirm={() => removingOption && removeOption.mutate(removingOption.id)}
      />
    </div>
  );
}
